import { Loader2, Pencil, RotateCw, Trash2 } from "lucide-react";

import { cn } from "@/shared/lib/utils";

export interface MediaCardActionRowProps {
  /**
   * 进内联重命名编辑态(通常来自 useInlineRename().begin)。
   * ★可选(E2E 0810 RP-WK-03):新闻线后端无改名端点、故意不实现 —— 此前 onRename 必填
   * 逼得调用方「缺改名就整行不渲染」,把删除/重试一起陪葬。与 onRetry 同口径:不传就不渲染。
   */
  onRename?: () => void;
  /** 删除(调用方自己弹 useConfirm 二次确认 —— 文案各线不同);不传 = 该线无删除端点,不渲染。 */
  onDelete?: () => void;
  /** 重试;不传 = 该线不支持重试(后端无 retry 端点的线)。 */
  onRetry?: () => void;
  /** 仅失败/部分失败可重试(成功的无需重出);onRetry 存在且此项为 true 才渲染重试按钮。 */
  canRetry?: boolean;
  /** 删除在途:置灰 + 拦点击,防连点重复提交。 */
  deleting?: boolean;
  /** 重试在途:转圈 + 拦点击(重试会新建扣费任务,防连点重复提交)。 */
  retrying?: boolean;
  className?: string;
}

/**
 * 媒体列表卡的常驻操作行(重试 / 重命名 / 删除)——海报 / 视频 / 分身**三线共用**。
 *
 * 2026-07-15 三线列表对齐。用户反馈「ai分身那边的卡片蛮空的,也没有删除」,而三条线的卡片操作
 * 各写各的(海报有重试/重命名/删除、视频只有一个删除图标、分身什么都没有)—— 抽成共用组件是为了
 * **下次不再漂**:再加一个动作时只有一处要改。
 *
 * 收藏(星)不在这一行:三线的星都长在标题行右侧(与标题同行),见 <MediaFavoriteToggle>。
 *
 * <b>不是 <button></b>:本行要嵌在卡片里,而卡片外壳常常自己就是个可点的 <button>(视频卡原来
 * 就是),button 套 button 是非法 HTML、且点子按钮会连带触发外层。故一律用 <button type="button">
 * 且要求调用方的卡壳是 <div>(视频卡已按此重构)。
 *
 * 常驻而非 hover 才显:2026-06-02 海报卡的结论 —— hover 才显在触屏上根本够不着。
 * shrink-0 + whitespace-nowrap:窄卡下中文标签不逐字换行致卡片高度参差(海报卡「派生」错行教训)。
 */
export function MediaCardActionRow({
  onRename,
  onDelete,
  onRetry,
  canRetry = false,
  deleting = false,
  retrying = false,
  className,
}: MediaCardActionRowProps) {
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    // 卡片封面/标题多半可点(预览/进详情),操作行的点击绝不能冒泡上去。
    e.stopPropagation();
    fn();
  };
  return (
    <div className={cn("mt-1 flex items-center gap-0.5 border-t pt-2", className)}>
      {onRetry && canRetry && (
        <button
          type="button"
          onClick={stop(onRetry)}
          disabled={retrying}
          className={cn(
            "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-1 text-2xs font-medium text-primary transition-colors hover:bg-primary/10",
            retrying && "pointer-events-none opacity-50"
          )}
          aria-label="重试"
          title="按原参数重新生成"
        >
          {retrying ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RotateCw className="h-3.5 w-3.5" />
          )}
          重试
        </button>
      )}
      {onRename && (
        <button
          type="button"
          onClick={stop(onRename)}
          className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-1 text-2xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          aria-label="重命名"
          title="重命名"
        >
          <Pencil className="h-3.5 w-3.5" />
          重命名
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={stop(onDelete)}
          disabled={deleting}
          className={cn(
            "ml-auto inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-1 text-2xs text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive",
            deleting && "pointer-events-none opacity-50"
          )}
          aria-label="删除"
          title="删除任务"
        >
          {deleting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" />
          )}
          删除
        </button>
      )}
    </div>
  );
}
